import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099LTCInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-LTC instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1099-LTC for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report long-term care insurance benefits."
          keywords=""
          canonical="resources/information-returns/form-1099-ltc-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-LTC</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-ltc-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-LTC at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-LTC{' '}
                        </strong>{' '}
                        reports payments made under long-term care insurance
                        contracts and accelerated death benefit provisions of
                        life insurance policies.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions including
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          payer information, policyholder information, insured
                          information, Account Number, Box 1
                        </strong>{' '}
                        (Gross Long-Term Care Benefits Paid),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>{' '}
                        (Accelerated Death Benefits Paid),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong> (Per
                        Diem or Reimbursed Basis),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>{' '}
                        (Qualified Contract Indicator), and{' '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>{' '}
                        (Chronically Ill or Terminally Ill Indicator).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-LTC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-LTC, &quot;Long-Term Care and Accelerated Death
                  Benefits&quot; is used by insurance companies, government
                  entities, and other payers to report payments made to
                  policyholders during the previous tax year. These payments
                  generally fall into two categories:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Long-Term Care (LTC) Benefits:</b> Reimbursements or
                    direct payments made to cover qualified long-term care
                    expenses, such as nursing home stays, assisted living
                    facilities, or home healthcare services.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Accelerated Death Benefits (ADBs):</b> Portions of a life
                    insurance policy&apos;s death benefit paid to the
                    policyholder while they are still alive, often due to a
                    terminal illness diagnosis.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The information reported on Form 1099-LTC helps the IRS
                  determine if any portion of these benefits is taxable for the
                  recipient.
                </Typography>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who receives a copy of Form 1099-LTC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  A copy of Form 1099-LTC must be sent to both the IRS and the
                  policyholder or the individual receiving the benefits. This
                  ensures that the IRS is informed of the payments made under a
                  long-term care insurance contract or for accelerated death
                  benefits.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  It also provides the recipient with the necessary information
                  to report these benefits on their tax return. Accurate
                  reporting helps both parties comply with tax regulations and
                  avoid potential issues with the IRS.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-LTC due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-LTC:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-LTC to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-LTC with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-LTC deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now for hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ltcinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-LTC with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must provide recipients with copies of Form 1099-LTC by
                  January 31st of the year following the tax year for which the
                  form is issued. This ensures recipients have the necessary
                  information to accurately file their tax returns on time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> * if the due date falls on the weekend or federal
                  holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Failure to meet these deadlines or providing inaccurate
                  information on Form 1099-LTC may result in IRS penalties.
                  Payers should take appropriate measures to ensure the timely
                  and accurate filing and distribution of Form 1099-LTC to avoid
                  potential penalties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-LTC penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-LTC
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-LTC penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', sm: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%', mb: { md: 0, sm: 1, xs: 2 } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-LTC forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 200,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ltcinstructions`}
                    >
                      E-file Form 1099-LTC
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-LTC Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-LTC.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-payer-details.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-payer-details.png" />
                  <img
                    src="/assets/images/form-1099-ltc-payer-details.png"
                    loading="lazy"
                    alt="Payer details"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the business name, complete address, and TIN (SSN if
                  you&apos;re an individual; EIN if you&apos;re a business)
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Policyholder details
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  <b>Definition:</b> The policyholder is the owner of the
                  insurance contract. This includes:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 10 }}>Individual owners</li>
                  <li style={{ marginBottom: 10 }}>
                    Owners who have sold or assigned the contract to a viatical
                    settlement provider
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    In group contracts, the certificate holder or similar
                    participant
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Report long-term care benefits to the policyholder, even if
                  payments were made to:
                </Typography>
                <ul style={{ marginBottom: 30, listStyleType: 'circle' }}>
                  <li style={{ marginBottom: 10 }}>The insured person</li>
                  <li style={{ marginBottom: 10 }}>
                    A third party (e.g., nursing home, caretaker, or physician)
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the policyholder is not an individual (e.g.,
                  a corporation), no reporting is required.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-policyholder-details.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-policyholder-details.png" />
                  <img
                    src="/assets/images/form-1099-ltc-policyholder-details.png"
                    loading="lazy"
                    alt="Policyholder details"
                    width={525}
                    height={233}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  Enter the policyholder’s name, complete address, and TIN (SSN
                  for an individual; EIN for a business).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    fontStyle: 'italic',
                  }}
                >
                  Account number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-account-number.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-account-number.png" />
                  <img
                    src="/assets/images/form-1099-ltc-account-number.png"
                    loading="lazy"
                    alt="Account number"
                    width={356}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  You must provide the account number in this situation:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 10 }}>
                    You have multiple accounts for the same recipient, and you
                    are filing more than one Form 1099-LTC for that recipient.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                    fontStyle: 'italic',
                  }}
                >
                  Insured
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-insured.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-insured.png" />
                  <img
                    src="/assets/images/form-1099-ltc-insured.png"
                    loading="lazy"
                    alt="Insured"
                    width={432}
                    height={284}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <b> Definition:</b> The insured is the person who is
                  chronically or terminally ill and for whom the long-term care
                  benefits are paid.
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  Enter the insured’s name, complete address, and TIN (SSN of
                  the individual).
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  <b>Important:</b> The policyholder and the insured may be the
                  same person in some cases.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Gross Long-Term Care Benefits Paid
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-gross-care.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-gross-care.png" />
                  <img
                    src="/assets/images/form-1099-ltc-gross-care.png"
                    loading="lazy"
                    alt="Box 1: Gross Long-Term Care Benefits Paid"
                    width={217}
                    height={105}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the total amount of long-term care benefits paid this
                  year, excluding accelerated death benefits. Include:
                </Typography>

                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    All payments made on a per diem basis
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    All payments made on any other periodic basis
                  </li>
                  <li style={{ marginBottom: 12 }}>All reimbursements</li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These payments may have been made to:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>The insured person</li>
                  <li style={{ marginBottom: 12 }}>The policyholder</li>
                  <li style={{ marginBottom: 12 }}>Third parties</li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> You do not need to determine whether the
                  benefits are taxable or non-taxable.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Accelerated Death Benefits Paid
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-accelerated-death.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-accelerated-death.png" />
                  <img
                    src="/assets/images/form-1099-ltc-accelerated-death.png"
                    loading="lazy"
                    alt="Box 2: Accelerated Death Benefits Paid"
                    width={217}
                    height={73}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of accelerated death benefits paid this
                  year under a life insurance contract. These payments must be
                  for an insured person who has been certified as either:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>Terminally ill, or</li>
                  <li style={{ marginBottom: 12 }}>Chronically ill</li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include in this amount:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Payments made directly to the insured
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Payments made on behalf of the insured
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any amount paid by a viatical settlement provider for the
                    purchase or assignment of the insured&apos;s death benefit
                    under a life insurance contract
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> &quot;Accelerated death benefits&quot; are life
                  insurance payouts made before the insured&apos;s death due to
                  severe illness.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: check if per diem or reimbursed basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-payment-method.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-payment-method.png" />
                  <img
                    src="/assets/images/form-1099-ltc-payment-method.png"
                    loading="lazy"
                    alt="Box 3: check if per diem or reimbursed basis"
                    width={217}
                    height={53}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check one box to indicate how the payments in Box 1 were made:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Per Diem or Other Periodic Basis
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Payments made at regular intervals
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Not based on actual expenses incurred
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Reimbursed Basis
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Payments made to cover actual expenses incurred
                  </li>
                </ul>
                <Alert severity="warning" icon={false} sx={{ mb: 4, mt: 0 }}>
                  <b> Note:</b>{' '}
                  <ol>
                    <li style={{ marginBottom: 12 }}>
                      For accelerated death benefits (Box 2), do not check
                      either box if the payments were made for a terminally ill
                      person.
                    </li>
                    <li style={{ marginBottom: 12 }}>Definitions:</li>
                    <ul>
                      <li style={{ marginBottom: 12 }}>
                        &quot;Per diem basis&quot; means regular payments made
                        regardless of actual expenses.
                      </li>
                      <li style={{ marginBottom: 12 }}>
                        &quot;Reimbursed basis&quot; means payments made to
                        cover specific, documented expenses.
                      </li>
                    </ul>
                  </ol>
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Qualified Contract (Optional)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-qualified-contract.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-qualified-contract.png" />
                  <img
                    src="/assets/images/form-1099-ltc-qualified-contract.png"
                    loading="lazy"
                    alt="Box 4: Qualified Contract (Optional)"
                    width={171}
                    height={53}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box to indicate whether long-term care insurance
                  benefits are paid from a qualified long-term care insurance
                  contract.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 5: Check if Chronically Ill or Terminally Ill (Optional)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-ltc-check-applicable.webp" />
                  <source srcSet="/assets/images/form-1099-ltc-check-applicable.png" />
                  <img
                    src="/assets/images/form-1099-ltc-check-applicable.png"
                    loading="lazy"
                    alt="Box 5: Check if Chronically Ill or Terminally Ill (Optional)"
                    width={432}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check the box to indicate whether the insured was
                    chronically or terminally ill. Also, enter the latest date
                    certified.{' '}
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If the insured was neither chronically nor terminally ill,
                    leave this box blank.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-LTC
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-LTC
                  </Link>
                  , either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method for its efficiency,
                  accuracy, and prompt processing notifications.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  With{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone
                  </Link>
                  , you can{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    e-file Form 1099-LTC
                  </span>{' '}
                  easily and securely. It{' '}
                  <span style={{ fontWeight: 600 }}>
                    streamlines the process
                  </span>
                  , allowing you to complete and transmit your form to the IRS
                  <span style={{ fontWeight: 600 }}> within minutes</span>. Here
                  how it works:
                </Typography>

                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the required information, including:
                  </li>
                  <ul>
                    {' '}
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s name, address, and Tax Identification Number
                      (TIN)
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Policyholder and insured&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Gross long-term care benefits paid and accelerated death
                      benefits paid
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Transmit the form electronically to the IRS.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>

                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-LTC from the official IRS website (
                    <Link
                      href="https://www.irs.gov/"
                      target="_blank"
                      sx={{ color: '#2020ff' }}
                    >
                      www.irs.gov
                    </Link>
                    ).
                  </li>

                  <li style={{ marginBottom: 16 }}>Print the form.</li>

                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The mailing address varies
                    based on your state. Refer to the &quot;Where to Send Form
                    1099-LTC -{' '}
                    <Link href="#mailaddltc" sx={{ color: '#0202ff' }}>
                      {' '}
                      Mailing Address&quot;
                    </Link>{' '}
                    section to find the correct address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by January 31st of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 8 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      timely arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  As an alternative, you can choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                    id="1099-s-mailing-address"
                  >
                    file Form 1099-LTC electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-LTC - Mailing address
                </Typography>

                <Typography variant="body1" id="mailaddltc" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-LTC varies depending on your business location. Below is
                  a table summarizing the mailing address for Form 1099-LTC:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-LTC to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-LTC with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-LTC
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-LTC:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Payer information:</li>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Name, address, and taxpayer identification number (TIN) of
                      the recipient
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>
                    Policyholder information:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Name, address, and taxpayer identification number (TIN) of
                      the policyholder
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>Insured information:</li>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Name, address, and taxpayer identification number (TIN) of
                      the insured
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>Transaction details:</li>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Gross long-term care benefits provided
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Accelerated death benefits paid
                    </li>
                  </ul>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-LTC using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, mt: 1.5 }}>
                  <b>Step 1: Complete Form 1099-LTC</b>
                  <br />
                  Open Form 1099-LTC and fill out the necessary fields with the
                  information you have gathered, including payer details,
                  policyholder details, insured details, and transaction
                  details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-LTC
                  to the IRS electronically through TaxZerone&apos;s secure
                  platform.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-LTC to the recipient. This
                  ensures they can accurately report the income on their tax
                  return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-LTC with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-ltc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-LTC
                  </Link>{' '}
                  with TaxZerone offers several advantages that streamline the
                  filing process and ensure compliance with IRS requirements:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> TaxZerone&apos;s system
                      checks your returns for errors or missing information to
                      ensure accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Email recipient copies:</b> Conveniently send
                      recipient copies of Form 1099-LTC via email and eliminate
                      manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure portal access (ZeroneVault):</b> Recipients can
                      access their return copies anytime, anywhere through the
                      secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive pricing:</b> TaxZerone offers
                      industry-leading pricing, with rates starting as low as
                      $0.59 per form, making it affordable for businesses of all
                      sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your Form 1099-LTC filing. Save time, reduce
                  errors, and provide a better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-LTC.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099ltcinstructions`}
              >
                E-file Form 1099-LTC
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099LTCInstructions;
